import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ScrollText, Search, ShieldCheck, Loader2, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_CLS = { staff: "text-[#7FC3FF]", admin: "text-emerald-400", manager: "text-amber-300" };

function detailText(d) {
  const parts = Object.entries(d || {}).filter(([, v]) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0));
  return parts.map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ");
}

// Tamper-evident activity log. Dealer admins see their dealership; Vinconex staff see everything (+ chain verification).
export function AuditLogPanel({ staff = false }) {
  const [rows, setRows] = useState(null);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [verify, setVerify] = useState(null);

  const load = () => {
    const params = new URLSearchParams({ limit: "100" });
    if (action !== "all") params.set("action", action);
    if (q.trim()) params.set("q", q.trim());
    api.get(`/audit?${params}`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  };
  useEffect(() => { api.get("/audit/actions").then(({ data }) => setActions(data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, q]);

  const groups = [...new Set(actions.map((a) => a.split(".")[0]))];

  const runVerify = async () => {
    setVerify("running");
    try {
      const { data } = await api.get("/platform/audit/verify");
      setVerify(data);
      if (data.ok) toast.success(`Audit chain intact — ${data.checked} events verified`);
      else toast.error(`Chain broken at event #${data.broken_at?.seq}`);
    } catch (e) {
      setVerify(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="audit-log-panel" className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-5 mb-6">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 font-heading font-bold"><ScrollText className="h-4 w-4 text-[#0A8CF2]" /> Activity log</div>
          <p className="text-xs text-slate-400 mt-1">Who did what, when and from where — sign-ins, settings, team, leads. Entries are hash-chained so they can't be edited after the fact; kept 13 months.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Email, IP, target…" data-testid="audit-search" className="h-9 pl-8 w-48 bg-slate-950 border-slate-800 text-slate-100 text-sm" />
          </div>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="audit-action-filter" className="h-9 w-40 bg-slate-950 border-slate-800 text-slate-200 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
              <SelectItem value="all">All activity</SelectItem>
              {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh" className="h-9 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"><RefreshCw className="h-3.5 w-3.5" /></Button>
          {staff && (
            <Button variant="outline" size="sm" onClick={runVerify} disabled={verify === "running"} data-testid="audit-verify-btn" className="h-9 border-emerald-800/60 bg-emerald-950/30 hover:bg-emerald-950/60 text-emerald-300">
              {verify === "running" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Verify chain</>}
            </Button>
          )}
        </div>
      </div>
      {verify && verify !== "running" && (
        <p data-testid="audit-verify-result" className={`text-xs mb-3 ${verify.ok ? "text-emerald-400" : "text-red-400"}`}>
          {verify.ok ? `Chain intact · ${verify.checked} events verified` : `Chain BROKEN at #${verify.broken_at?.seq} (${verify.broken_at?.action})`}
        </p>
      )}
      {rows === null ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#0A8CF2]" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8" data-testid="audit-empty">No activity matches.</p>
      ) : (
        <div className="max-h-[420px] overflow-y-auto rounded-lg border border-slate-800 divide-y divide-slate-800/80" data-testid="audit-list">
          {rows.map((e) => (
            <div key={e.id} data-testid={`audit-${e.id}`} className="grid grid-cols-[auto_1fr] sm:grid-cols-[110px_1fr] gap-x-3 gap-y-0.5 px-3 py-2 text-xs bg-slate-950/60">
              <div className="text-slate-500 font-mono-tech" title={new Date(e.at).toLocaleString()}>{timeAgo(e.at)}</div>
              <div className="min-w-0">
                <span className="font-mono-tech font-semibold text-slate-200">{e.action}</span>
                {e.actor_email && <span className={`ml-2 ${ROLE_CLS[e.actor_role] || "text-slate-400"}`}>{e.actor_email}</span>}
                {e.target_label && <span className="ml-2 text-slate-300">→ {e.target_label}</span>}
                {!e.target_label && e.target_type && <span className="ml-2 text-slate-500">→ {e.target_type} {e.target_id?.slice(-6)}</span>}
                {(e.ip || e.device) && <span className="ml-2 text-slate-600">{[e.ip, e.device].filter(Boolean).join(" · ")}</span>}
                {detailText(e.details) && <div className="text-slate-500 truncate">{detailText(e.details)}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
